import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Plus, Pencil, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import HRLayout from '../../components/layout/HRLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminMandatoryTrainingPage
 *
 * Admin curates the list of mandatory training links (name + URL).
 * Kept intentionally minimal — one screen, one dialog, no categories,
 * no ordering. Purpose is to stop admins re-sending URLs on WhatsApp.
 */
export default function AdminMandatoryTrainingPage({ hrMode = false }) {
  const Layout = hrMode ? HRLayout : AdminLayout;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add new
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/mandatory-trainings`);
      setRows(data || []);
    } catch (err) {
      toast.error('Failed to load trainings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setName('');
    setUrl('');
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row.name);
    setUrl(row.url);
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName) {
      toast.error('Please enter a course name.');
      return;
    }
    if (!/^https?:\/\//i.test(trimmedUrl)) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/mandatory-trainings/${editing.id}`,
          { name: trimmedName, url: trimmedUrl });
        toast.success('Course updated.');
      } else {
        await axios.post(`${API}/admin/mandatory-trainings`,
          { name: trimmedName, url: trimmedUrl });
        toast.success('Course added.');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save course.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/admin/mandatory-trainings/${toDelete.id}`);
      toast.success('Course deleted.');
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error('Failed to delete course.');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-mandatory-training-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-yellow-500" />
            Mandatory Training
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Curate the training links every employee must complete. Keeps
            everything in one place instead of scattered on WhatsApp.
          </p>
        </div>
        <Button onClick={openAdd} data-testid="add-training-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Course
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No training courses yet. Click <span className="font-semibold text-foreground">Add Course</span> to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="training-grid">
          {rows.map((row) => (
            <Card key={row.id} data-testid={`training-card-${row.id}`}>
              <CardHeader>
                <CardTitle className="text-base flex items-start gap-2">
                  <BookOpen className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <span className="break-words">{row.name}</span>
                </CardTitle>
                <CardDescription className="text-xs break-all">
                  {row.url}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(row.url, '_blank', 'noopener,noreferrer')}
                  data-testid={`open-training-${row.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(row)}
                  data-testid={`edit-training-${row.id}`}
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                {!hrMode && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setToDelete(row)}
                  data-testid={`delete-training-${row.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="training-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Course' : 'Add Course'}</DialogTitle>
            <DialogDescription>
              Enter the course name and its full URL. The link must start with http:// or https://
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="training-name">Course Name</Label>
              <Input
                id="training-name"
                data-testid="training-name-input"
                placeholder="e.g. ACT Awareness"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-url">Course URL</Label>
              <Input
                id="training-url"
                data-testid="training-url-input"
                placeholder="https://example.com/course"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                maxLength={1000}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="training-save-btn">
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (editing ? 'Save Changes' : 'Add Course')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this course?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{toDelete?.name}&quot; will be removed from the Mandatory Training list.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-training-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </Layout>
  );
}
